import { useEffect, useState, useContext } from 'react';
import { ref } from 'firebase/storage';
import { doc, Timestamp, setDoc, addDoc, collection } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import {
  FirebaseStorage,
  FirestoreDB,
  LogAnalyticsEvent,
} from '../../../utils/firebase/firebase';
import { UploadFileToStorage } from '../../../utils/firebase/helper';
import AuthContext from '../../../contexts/AuthContext';
import usePatientsForDoctor from '../../../hooks/usePatientsForDoctor';
import useTeachersForOneStudent from '../../../hooks/useTeachersForOneStudent';
import useStudentsForOneTeacher from '../../../hooks/useStudentsForOneTeacher';
import useFetchAllAttachmentsByUser from '../../../hooks/useFetchAllAttachmentsByUser';
import { fetchingTeachersForStudents } from '../../../api/teachers';
import { ACM } from '../../../constants/UserRoles';
import { ANALYTICS_KEYS } from '../../../constants/AnalyticKeys';
import { usePreRegisteredUsers } from '../../../contexts/userDataContext';

const useUploadDocument = (user, typeOfUser) => {
  const queryClient = useQueryClient();
  const { setOpenVideoUpload } = useContext(AuthContext);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [patientData, setPatientData] = useState([]);
  const [studentData, setStudentData] = useState([]);
  const [SelectedPatient, SetSelectedPatient] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [shareWithTeacherIds, setShareWithTeacherIds] = useState([]);
  const [extractDocumentsFromGoals, setExtractDocumentsFromGoals] =
    useState(false);
  const [extractOtherInfo, setExtractOtherInfo] = useState(false);
  const [otherDocumentType, setOtherDocumentType] = useState('');
  const [typeOfAssessment, setTypeOfAssessment] = useState('');
  const [otherAssessmentType, setOtherAssessmentType] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [information, setInformation] = useState({ notes: '' });
  const [date, setDate] = useState(null);
  const [error, setError] = useState({ show: false, message: '' });
  const [typeOfDocument, setTypeOfDocument] = useState('');

  // eslint-disable-next-line no-unused-vars
  const { data: allattachments, refetch: refetchAllAttachments } =
    useFetchAllAttachmentsByUser(user?.uid, typeOfUser);

  const { data: patients = [] } = usePatientsForDoctor(user?.uid);
  const { data: teacherData = [] } = useTeachersForOneStudent(
    user?.uid,
    typeOfUser
  );
  const { data: students = [] } = useStudentsForOneTeacher(
    user?.uid,
    typeOfUser
  );
  const { data: preRegisteredUsers = [] } = usePreRegisteredUsers();

  const isPreRegistered = (id) => {
    return preRegisteredUsers.some((student) => student.id === id);
  };

  useEffect(() => {
    const fetchTeachers = async () => {
      if (!selectedStudent) return;
      const result = await fetchingTeachersForStudents(selectedStudent);
      const filteredData = result?.filter((item) => item.uid !== user?.uid);
      setTeachers(filteredData);
    };
    fetchTeachers();
  }, [selectedStudent, preRegisteredUsers, user?.uid]);

  const dateChangeHandler = (newValue) => {
    setDate(newValue._d);
    if (newValue && newValue._d <= new Date()) {
      setDate(newValue._d);
      setError({ show: false, message: '' });
    } else {
      setDate(null);
      setError({
        show: true,
        message:
          'Select Valid Date, Video taken time cannot be greater than current time.',
      });
    }
  };

  const showToastMessage = (status) => {
    if (status === 'success') {
      toast.success('Your upload was successful', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } else {
      toast.error('Your upload was Failed', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    file && setUploadedFile(file);
  };

  const handleClose = () => {
    setOpenVideoUpload(false);
    setUploadedFile(null);
    setTypeOfDocument('');
    setShareWithTeacherIds([]);
    setExtractDocumentsFromGoals(false);
    setExtractOtherInfo(false);
    setSelectedStudent('');
    SetSelectedPatient('');
    setInformation({ notes: '' });
    setDate(null);
  };

  useEffect(() => {
    setUploadedFile(null);
  }, []);

  const validateInputsForAttchment = () => {
    const errors = {};
    if (!uploadedFile) errors.uploadedFile = 'Please select a file to upload.';
    if (!date) errors.date = 'Please select a valid date.';
    if (date >= new Date()) errors.date = 'Please select a valid date.';
    return { isValid: Object.keys(errors).length === 0, errors };
  };

  const addAttachmentsNotification = async (attachmentID, selectedUserId) => {
    try {
      // Use correct DocumentUploaded source
      // eslint-disable-next-line no-unused-vars
      let notificationSource = `${typeOfUser.toLowerCase()}DocumentUploaded`;

      if (ACM[typeOfUser]?.isPatient) {
        // Patient uploaded document - notify their doctors AND teachers
        const doctors = user?.doctorDetails || [];
        const teachers = user?.teacherDetails || [];

        // Notify all doctors
        for (const doctor of doctors) {
          if (doctor.doctorId) {
            const params = {
              title: 'Patient uploaded a new document',
              description: '',
              notifyTo: doctor?.email,
              sentBy: user?.email,
              userName: user?.fullname,
              userPhotoURL: user?.photoURL,
              attachmentID: attachmentID,
            };
            await addDoc(
              collection(
                FirestoreDB,
                'notifications',
                doctor.doctorId,
                'messages'
              ),
              {
                infoMessage: btoa(JSON.stringify(params)),
                notificationSource: 'patientDocumentUploaded',
                userId: user?.uid,
                isEncrypted: false,
                readStatus: false,
                createdDate: Timestamp.now(),
              }
            );
          }
        }

        // Notify all teachers
        for (const teacher of teachers) {
          if (teacher.id || teacher.teacherId) {
            const params = {
              title: 'Patient uploaded a new document',
              description: '',
              notifyTo: teacher?.email,
              sentBy: user?.email,
              userName: user?.fullname,
              userPhotoURL: user?.photoURL,
              attachmentID: attachmentID,
            };
            await addDoc(
              collection(
                FirestoreDB,
                'notifications',
                teacher.id || teacher.teacherId,
                'messages'
              ),
              {
                infoMessage: btoa(JSON.stringify(params)),
                notificationSource: 'patientDocumentUploaded',
                userId: user?.uid,
                isEncrypted: false,
                readStatus: false,
                createdDate: Timestamp.now(),
              }
            );
          }
        }
      } else if (ACM[typeOfUser]?.isStudent) {
        // Student uploaded document - notify their teachers (using hook data)
        // Students don't have teacherDetails in user object, so use teacherData from hook

        // Notify all teachers from hook data
        for (const teacher of teacherData) {
          if (teacher.id) {
            const params = {
              title: 'Student uploaded a new document',
              description: '',
              notifyTo: teacher?.email,
              sentBy: user?.email,
              userName: user?.fullname || user?.name,
              userPhotoURL: user?.photoURL,
              attachmentID: attachmentID,
            };
            await addDoc(
              collection(FirestoreDB, 'notifications', teacher.id, 'messages'),
              {
                infoMessage: btoa(JSON.stringify(params)),
                notificationSource: 'studentDocumentUploaded',
                userId: user?.uid,
                isEncrypted: false,
                readStatus: false,
                createdDate: Timestamp.now(),
              }
            );
          }
        }
      } else if (ACM[typeOfUser]?.isDoctor && !ACM[typeOfUser]?.isTeacher) {
        // Doctor uploaded document - notify the selected patient AND their teachers
        const selectedPatientDetails = patients.find(
          (each) => each?.patientId === selectedUserId
        );
        if (selectedPatientDetails) {
          // Notify the patient
          const params = {
            title: 'Healthcare professional uploaded a new document',
            description: '',
            notifyTo: selectedPatientDetails?.patientInfo?.email,
            sentBy: user?.email,
            userName: user?.fullname || user?.name,
            userPhotoURL: user?.photoURL,
            attachmentID: attachmentID,
          };
          await addDoc(
            collection(
              FirestoreDB,
              'notifications',
              selectedPatientDetails.patientId,
              'messages'
            ),
            {
              infoMessage: btoa(JSON.stringify(params)),
              userId: user?.uid,
              notificationSource: 'doctorDocumentUploaded',
              isEncrypted: false,
              readStatus: false,
              createdDate: Timestamp.now(),
            }
          );

          // Also notify patient's teachers if they have any
          const patientTeachers = selectedPatientDetails.teacherDetails || [];
          for (const teacher of patientTeachers) {
            if (teacher.id || teacher.teacherId) {
              const teacherParams = {
                title:
                  'Healthcare professional uploaded a document for your patient',
                description: '',
                notifyTo: teacher?.email,
                sentBy: user?.email,
                userName: user?.fullname || user?.name,
                userPhotoURL: user?.photoURL,
                attachmentID: attachmentID,
              };
              await addDoc(
                collection(
                  FirestoreDB,
                  'notifications',
                  teacher.id || teacher.teacherId,
                  'messages'
                ),
                {
                  infoMessage: btoa(JSON.stringify(teacherParams)),
                  userId: user?.uid,
                  notificationSource: 'doctorDocumentUploaded',
                  isEncrypted: false,
                  readStatus: false,
                  createdDate: Timestamp.now(),
                }
              );
            }
          }
        }
      } else if (ACM[typeOfUser]?.isTeacher) {
        // Teacher uploaded document - notify the selected student (using hook data)
        // Teachers don't have access to student's doctor relationships in the simplified hook data
        const selectedStudentDetails = students?.find(
          (each) => each?.id === selectedUserId
        );

        if (selectedStudentDetails) {
          // Notify the student
          const params = {
            title: 'Teacher uploaded a new document',
            description: '',
            notifyTo: selectedStudentDetails?.email,
            sentBy: user?.email,
            userName: user?.fullname || user?.name,
            userPhotoURL: user?.photoURL,
            attachmentID: attachmentID,
          };

          await addDoc(
            collection(
              FirestoreDB,
              'notifications',
              selectedStudentDetails.id,
              'messages'
            ),
            {
              infoMessage: btoa(JSON.stringify(params)),
              userId: user?.uid,
              notificationSource: 'teacherDocumentUploaded',
              isEncrypted: false,
              readStatus: false,
              createdDate: Timestamp.now(),
            }
          );

          // Note: Student's doctors are not notified because the hook data doesn't include
          // student relationship data. This would require a separate API call to get full student details.
        }
      }
    } catch (e) {
      console.log('Error while adding notification', e);
    }
  };

  const attachmentUploadFile = async (id) => {
    try {
      const validation = validateInputsForAttchment();
      if (validation.isValid) {
        setIsLoading(true);
        setError({ show: false, message: '' });

        const userId =
          ACM[typeOfUser]?.isDoctor || ACM[typeOfUser]?.isTeacher
            ? id
            : user?.uid;

        // if (
        //   uploadedFile &&
        //   (uploadedFile.type === 'application/pdf' ||
        //     uploadedFile.type === 'application/vnd.ms-excel' ||
        //     uploadedFile.type ===
        //       'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        //     uploadedFile.type === 'text/csv')
        // ) {
        //   const attachmentID = uuidv4();
        //   const blob = new Blob([uploadedFile], { type: uploadedFile.type });
        //   const storageRef = ref(
        //     FirebaseStorage,
        //     `historicalData/${userId}/${attachmentID}/${
        //       typeOfDocument === 'Summary' ? 'attachments' : 'IEPattachments'
        //     }/${uploadedFile.name}`
        //   );

        //   const result = await UploadFileToStorage(storageRef, blob);

        if (
          uploadedFile &&
          (uploadedFile.type === 'application/pdf' ||
            uploadedFile.type === 'application/vnd.ms-excel' ||
            uploadedFile.type ===
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
            uploadedFile.type === 'text/csv')
        ) {
          const attachmentID = uuidv4();
          const blob = new Blob([uploadedFile], { type: uploadedFile.type });

          // Determine correct folder path based on document type
          let folderName = '';
          if (typeOfDocument === 'Summary') {
            folderName = 'attachments';
          } else if (typeOfDocument === 'Assessment') {
            folderName = 'assessmentAttachments';
          } else if (typeOfDocument === 'Other') {
            folderName = 'otherAttachments';
          } else {
            folderName = 'IEPattachments';
          }

          // Updated storage reference
          const storageRef = ref(
            FirebaseStorage,
            `historicalData/${userId}/${attachmentID}/${folderName}/${uploadedFile.name}`
          );

          const result = await UploadFileToStorage(storageRef, blob);

          if (result.status) {
            const url = result.data;
            const params = {
              fileName: uploadedFile.name,
              fileSize: uploadedFile.size,
              fileType: uploadedFile.type,
              url: url,
              id: attachmentID,
              userName: user?.fullname,
              userType: typeOfUser,
              typeOfDocument: typeOfDocument,
              typeOfSpecificDocument:
                typeOfDocument === 'Other' ? otherDocumentType : null,
            };

            const docData = {
              info: btoa(JSON.stringify(params)),
              isEncrypted: false,
              notes: btoa(information?.notes || ''),
              isDeleted: false,
              createdAt: Timestamp.now(),
              selectedDate: date || null,
              shareWithTeacherIds: shareWithTeacherIds,
              typeOfDocument:
                // typeOfDocument === 'Other' ? otherDocumentType : typeOfDocument,
                typeOfDocument === 'Other' ? 'Other' : typeOfDocument,
              extractDocumentsFromGoals: extractDocumentsFromGoals,
              extractOtherInfo: extractOtherInfo,
              ...(typeOfDocument === 'Assessment' && {
                typeOfAssessment:
                  typeOfAssessment === 'Other'
                    ? otherAssessmentType
                    : typeOfAssessment,
              }),
              UploadedBy: user?.uid,
              TypeOfUserUploadBy: typeOfUser,
              isPendingGoal: extractDocumentsFromGoals ? true : false,
            };

            // if (typeOfDocument === 'Summary') {
            //   await setDoc(doc(FirestoreDB, 'historicalData', id), {
            //     documentsData: [],
            //   });
            //   await setDoc(
            //     doc(
            //       FirestoreDB,
            //       'historicalData',
            //       id,
            //       'attachments',
            //       attachmentID
            //     ),
            //     docData
            //   );
            // } else {
            //   await setDoc(
            //     doc(
            //       FirestoreDB,
            //       'historicalData',
            //       id,
            //       'IEPattachments',
            //       attachmentID
            //     ),
            //     docData
            //   );
            // }

            if (typeOfDocument === 'Summary') {
              await setDoc(doc(FirestoreDB, 'historicalData', id), {
                documentsData: [],
              });
              await setDoc(
                doc(
                  FirestoreDB,
                  'historicalData',
                  id,
                  'attachments',
                  attachmentID
                ),
                docData
              );
            } else if (typeOfDocument === 'Assessment') {
              await setDoc(
                doc(
                  FirestoreDB,
                  'historicalData',
                  id,
                  'assessmentAttachments',
                  attachmentID
                ),
                docData
              );
            } else if (typeOfDocument === 'Other') {
              await setDoc(
                doc(
                  FirestoreDB,
                  'historicalData',
                  id,
                  'otherAttachments',
                  attachmentID
                ),
                docData
              );
            } else {
              await setDoc(
                doc(
                  FirestoreDB,
                  'historicalData',
                  id,
                  'IEPattachments',
                  attachmentID
                ),
                docData
              );
            }

            const documentUploadKey = ACM[typeOfUser]?.isDoctor
              ? ANALYTICS_KEYS.DASHBOARD.DOCTORDOCUMENTUPLOAD
              : ANALYTICS_KEYS.DASHBOARD.PATIENTDOCUMENTUPLOAD;
            const userType = typeOfUser ? typeOfUser.toLowerCase() : 'unknown';
            const message = `${userType} successfully uploaded a document`;
            LogAnalyticsEvent(documentUploadKey, {
              attachmentID: attachmentID,
              message: message,
            });
            await queryClient.invalidateQueries({
              queryKey: ['fetchAllAttachmentsByUser', user?.uid, typeOfUser],
              refetchType: 'all',
            });
            await queryClient.invalidateQueries({
              queryKey: ['oneStudentData', id],
              refetchType: 'all',
            });

            addAttachmentsNotification(attachmentID, id);
            showToastMessage('success');
            handleClose();
          } else {
            toast.error('Error uploading file. Please try again.', {
              position: toast.POSITION.BOTTOM_RIGHT,
            });
          }
        } else if (date >= new Date()) {
          toast.error('Please select a valid date', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        } else {
          toast.error('Please upload only documents!', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }
        setIsLoading(false);
      } else {
        setError({ show: true, message: 'Enter all the above values.' });
      }
    } catch (error) {
      console.error('Error message: ', error);
      setIsLoading(false);
      showToastMessage('error');
    }
  };

  const attachmentSaveHandler = () => {
    const id = ACM[typeOfUser]?.isDoctor
      ? SelectedPatient
      : selectedStudent || user.uid;
    attachmentUploadFile(id);
  };

  useEffect(() => {
    if (patients && patients.length > 0) {
      const dataArray = patients.map((each) => ({
        ...each.patientInfo,
        id: each.patientId,
      }));
      setPatientData(dataArray);
    }
  }, [patients]);

  useEffect(() => {
    if (students) {
      const dataArray = students.map((each) => ({
        id: each.id,
        name: each.name,
        email: each.email,
        parentName: each.parentName,
        schoolName: each.schoolName,
        photoURL: each.photoURL,
      }));
      setStudentData(dataArray);
    }
  }, [students]);

  const capitalizeWords = (name) => {
    return name
      ?.split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const truncateName = (name, limit = 10) => {
    const capitalized = capitalizeWords(name);
    if (capitalized?.length > limit) {
      return `${capitalized.slice(0, limit)}...`;
    }
    return capitalized;
  };

  const mergedData = ACM[typeOfUser]?.isTeacher
    ? [...students, ...preRegisteredUsers]
    : [...patientData, ...preRegisteredUsers];

  return {
    uploadedFile,
    patientData,
    studentData,
    SelectedPatient,
    SetSelectedPatient,
    selectedStudent,
    setSelectedStudent,
    isLoading,
    shareWithTeacherIds,
    setShareWithTeacherIds,
    extractDocumentsFromGoals,
    setExtractDocumentsFromGoals,
    extractOtherInfo,
    setExtractOtherInfo,
    otherDocumentType,
    setOtherDocumentType,
    typeOfAssessment,
    setTypeOfAssessment,
    otherAssessmentType,
    setOtherAssessmentType,
    teachers,
    information,
    setInformation,
    date,
    setDate,
    error,
    typeOfDocument,
    setTypeOfDocument,
    handleFileChange,
    handleClose,
    dateChangeHandler,
    attachmentSaveHandler,
    isPreRegistered,
    mergedData,
    truncateName,
  };
};

export default useUploadDocument;
